import { InterpolatedTranslatePipe } from '@web/common';
import { animate, AUTO_STYLE, state, style, transition, trigger } from '@angular/animations';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { NotificationEntity, NotificationStore } from '@web/service';
import { filter } from 'rxjs';
import { SideNavigationMenuItem } from '../../model/side-navigation.model';

@Component({
  selector: 'cas-side-navigation-menu-item',
  template: `
    <ng-template #itemStyling>
      <div class="d-flex justify-content-center align-items-center">
        <fa-icon
          [icon]="[iconStyle(), menuItem().icon]"
          size="1x"
          [fixedWidth]="true"></fa-icon>
      </div>
      <span class="visually-hidden">Icon-only</span>
      <span
        class="my-auto overflow-hidden col fs-normal"
        [class.fw-medium]="!isActiveRoute()"
        [class.fw-bold]="isActiveRoute()"
        [@titleState]="state()">
        {{ menuItem().label | translate }}
      </span>
      @if (notification(); as notification) {
        <div
          class="bullet bg-danger"
          [class.wide]="wide()">
          <span
            [ngbTooltip]="notification.label | translate: { value: notification.count }"
            container="body">
            {{ notification.count ?? '&nbsp;' }}
          </span>
        </div>
      }
    </ng-template>
    @let item = menuItem();
    @if (item) {
      @if (item.routerLink) {
        <a
          class="btn p-0 mb-6 d-flex align-items-center position-relative border-start-0 text-start"
          [class.btn-cloud-white]="!isActiveRoute()"
          [class.text-ink-light]="!isActiveRoute()"
          [class.btn-primary-light]="isActiveRoute()"
          [class.text-primary]="isActiveRoute()"
          [routerLink]="item.routerLink"
          [queryParams]="item.queryParams">
          <ng-container [ngTemplateOutlet]="itemStyling"></ng-container>
        </a>
      }
      @if (item.externalURL) {
        <a
          class="btn p-0 mb-6 d-flex align-items-center position-relative border-start-0 text-start"
          [class.btn-cloud-white]="!isActiveRoute()"
          [class.text-ink-light]="!isActiveRoute()"
          [class.btn-primary-light]="isActiveRoute()"
          [class.text-primary]="isActiveRoute()"
          [href]="item.externalURL"
          target="_blank">
          <ng-container [ngTemplateOutlet]="itemStyling"></ng-container>
        </a>
      }
    }
  `,
  styleUrls: ['./side-navigation-menu-item.component.scss'],
  animations: [
    trigger('titleState', [
      state(
        'narrow',
        style({
          display: 'none',
        }),
      ),
      state(
        'wide',
        style({
          width: AUTO_STYLE,
        }),
      ),
      transition('narrow <=> wide', animate('0ms ease-out')),
    ]),
  ],
  imports: [FontAwesomeModule, InterpolatedTranslatePipe, NgbTooltip, RouterModule, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavigationMenuItemComponent {
  readonly #notificationStore = inject(NotificationStore);
  readonly #router = inject(Router);

  readonly state = input.required<'wide' | 'narrow' | 'closed' | 'opened'>();
  readonly menuItem = input.required<SideNavigationMenuItem>();
  readonly #url = computed(() => {
    const routerlink = this.menuItem().routerLink;
    return routerlink && this.#router.createUrlTree(routerlink);
  });

  readonly notification = computed<NotificationEntity | undefined>(() => {
    const menuItem = this.menuItem();
    return this.#notificationStore.notifications().find(notification => notification.id === menuItem.notificationId);
  });
  readonly wide = computed(() => this.state() === 'wide');

  readonly isActiveRoute: Signal<boolean>;

  readonly iconStyle = computed<'far' | 'fas'>(() => (this.isActiveRoute() ? 'fas' : 'far'));

  constructor() {
    const navigated = toSignal(this.#router.events.pipe(filter(event => event instanceof NavigationEnd)));
    this.isActiveRoute = computed(() => {
      const url = this.#url();
      navigated();
      return (
        !!url &&
        this.#router.isActive(url, {
          paths: 'subset',
          fragment: 'ignored',
          matrixParams: 'ignored',
          queryParams: 'ignored',
        })
      );
    });
  }
}
